import React, { useEffect, useState } from "react";

import { ReactComponent as CloseIcon } from "../../assets/Close.svg";
import { useDispatch } from "react-redux";
import { popFirstToastMessage } from "../../redux/toastMessages/toastMessages.actions";

// Small icon glyphs — check/x/info — inline so we don't add SVG assets
// just for one component. Colours pulled from the app's Tailwind palette
// so the icon reads as part of the same theme, not an external toast lib.
//   success.main = #43E400, danger.main = #FF7979, primary.main = #008CFF
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="6" fill="#43E400" />
    <path
      d="M4.5 7.2l1.7 1.7 3.3-3.5"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const IconX = ({ onRich }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="6" fill={onRich ? "rgba(255,255,255,0.2)" : "#C62828"} />
    <path
      d="M5 5l4 4M9 5l-4 4"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);
const IconInfo = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="6" fill="#008CFF" />
    <path
      d="M7 6v3.5M7 4.5v.01"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const iconFor = type => {
  if (type === "success") return <IconCheck />;
  if (type === "failure") return <IconX onRich />;
  return <IconInfo />;
};

const accentFor = type => {
  if (type === "success") return "#43E400";
  return "#008CFF";
};

const surfaceFor = type => {
  if (type === "failure") {
    return {
      backgroundColor: "#C62828",
      color: "#FFFFFF",
      border: "1px solid #B71C1C",
      borderLeft: "1px solid #B71C1C",
      messageColor: "#FFFFFF",
      closeColor: "rgba(255,255,255,0.85)",
      closeHover: "rgba(255,255,255,0.12)",
    };
  }
  return {
    backgroundColor: "#FFFFFF",
    color: "#3B3B3B",
    border: "1px solid #E1E1E1",
    borderLeft: `3px solid ${accentFor(type)}`,
    messageColor: "#3B3B3B",
    closeColor: "#818181",
    closeHover: undefined,
  };
};

const Toast = ({ message, type }) => {
  const dispatch = useDispatch();
  // Two-phase: `mounted` triggers the enter animation once we're in the DOM;
  // `leaving` triggers the exit animation before we tell redux to drop us.
  const [mounted, setMounted] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const dismiss = () => {
    if (leaving) return;
    setLeaving(true);
    // Match the CSS exit duration below (200ms) before actually popping
    // from state — lets the fade-out play.
    setTimeout(() => dispatch(popFirstToastMessage()), 200);
  };

  useEffect(() => {
    // Fire the enter animation on next tick so the initial hidden style
    // has a chance to be committed to the DOM.
    const raf = requestAnimationFrame(() => setMounted(true));
    const auto = setTimeout(dismiss, 3000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(auto);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const surface = surfaceFor(type);

  return (
    <div
      role="status"
      aria-live="polite"
      className={`transition-all duration-200 ease-out transform ${
        mounted && !leaving
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-2"
      }`}
      style={{
        backgroundColor: surface.backgroundColor,
        color: surface.color,
        border: surface.border,
        borderLeft: surface.borderLeft,
        borderRadius: 10,
        boxShadow: "0 8px 24px rgba(20,24,29,0.08)",
        padding: "10px 14px",
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        minWidth: 220,
        maxWidth: 420,
      }}>
      <span className="shrink-0 flex items-center">{iconFor(type)}</span>
      <span
        className="text-sm font-semibold leading-tight flex-1"
        style={{ color: surface.messageColor }}>
        {message}
      </span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 p-1 rounded transition-colors"
        style={{ color: surface.closeColor }}
        onMouseEnter={event => {
          if (surface.closeHover) {
            event.currentTarget.style.backgroundColor = surface.closeHover;
          }
        }}
        onMouseLeave={event => {
          event.currentTarget.style.backgroundColor = "transparent";
        }}>
        <CloseIcon
          className="h-3 w-3 fill-current"
          aria-hidden="true"
        />
      </button>
    </div>
  );
};

export default Toast;
